import { gql } from '@apollo/client'
import { BodyText, Heading } from '@farol-ds/react'
import { Price } from '@jusbrasil-web/shared-price'

import { BenefitsList } from '../../benefits-list'
import { CheckoutButton } from '../../checkout-button'
import { OneClickButton } from '../../one-click-button'
import styles from '../lawsuit-name-monitoring-lock.module.scss'
import {
  LawsuitNameMonitoringLockContent_OfferLockDataFragment,
  LawsuitNameMonitoringLockContent_ViewerFragment,
} from './__generated__/lawsuit-name-monitoring-lock-content.graphql'

export interface LawsuitNameMonitoringLockContentProps {
  offerLockData: LawsuitNameMonitoringLockContent_OfferLockDataFragment
  viewer: LawsuitNameMonitoringLockContent_ViewerFragment
}

export function LawsuitNameMonitoringLockContent({
  offerLockData,
  viewer,
}: LawsuitNameMonitoringLockContentProps) {
  const { header } = offerLockData
  const { title, subtitle } = header

  return (
    <div>
      <Heading className={styles['lawsuit-name-monitoring-title']} size="lg" asChild>
        <h3>{title}</h3>
      </Heading>
      <BodyText size="md" className={styles['lawsuit-name-monitoring-subtitle']}>
        <span dangerouslySetInnerHTML={{ __html: subtitle }} />
      </BodyText>
      <div className={styles.nameMonitoringLockCard}>
        <LawsuitNameMonitoringCard offerLockData={offerLockData} viewer={viewer} />
      </div>
    </div>
  )
}

function LawsuitNameMonitoringCard({
  offerLockData,
  viewer,
}: {
  offerLockData: LawsuitNameMonitoringLockContent_OfferLockDataFragment
  viewer: LawsuitNameMonitoringLockContent_ViewerFragment
}) {
  const { body } = offerLockData || {}
  const {
    cards: [card],
  } = body || {}

  if (!card) {
    return null
  }

  const { priceTitle, priceText, frequency } = card.offer ?? {}

  return card.creditCard?.ccToken ? (
    <div className={styles.card}>
      <BenefitsList
        offerLockCard={card}
        listContainerClassName={styles['lawsuit-name-monitoring-benefits']}
      />
      <div className={styles['lawsuit-name-monitoring-card-content']}>
        <CheckoutButton offerLockCard={card} kind="primary" viewer={viewer} />
        <Price align="center" size="md" className={styles['lawsuit-name-monitoring-price']}>
          {priceTitle && <Price.Title>{priceTitle}</Price.Title>}
          <Price.Value>
            {priceText}
            {frequency && <Price.ValueFrequency>{frequency}</Price.ValueFrequency>}
          </Price.Value>
        </Price>
        <OneClickButton offerLockCard={card} viewer={viewer} kind="primary" />
      </div>
    </div>
  ) : (
    <div className={styles.card}>
      <Price align="center" size="md" className={styles['lawsuit-name-monitoring-price']}>
        {priceTitle && <Price.Title>{priceTitle}</Price.Title>}
        <Price.Value>
          {priceText}
          {frequency && <Price.ValueFrequency>{frequency}</Price.ValueFrequency>}
        </Price.Value>
      </Price>
      <CheckoutButton offerLockCard={card} kind="primary" viewer={viewer} />
      <BenefitsList
        offerLockCard={card}
        listContainerClassName={styles['lawsuit-name-monitoring-benefits']}
      />
    </div>
  )
}

LawsuitNameMonitoringLockContent.fragments = {
  offerLockData: gql`
    fragment LawsuitNameMonitoringLockContent_offerLockData on OfferLockData {
      header {
        title
        subtitle
      }
      body {
        cards {
          ...BenefitsList_offerLockCard
          title
          subtitle
          offer {
            priceTitle
            priceText
            frequency
          }
          ...OneClickButton_offerLockCard
          ...CheckoutButton_offerLockCard
        }
      }
    }
    ${OneClickButton.fragments.offerLockCard}
    ${BenefitsList.fragments.offerLockCard}
    ${CheckoutButton.fragments.offerLockCard}
  `,
  viewer: gql`
    fragment LawsuitNameMonitoringLockContent_viewer on Profile {
      pid
      ...OneClickButton_viewer
      ...CheckoutButton_viewer
    }
    ${OneClickButton.fragments.viewer}
    ${CheckoutButton.fragments.viewer}
  `,
}
